import { useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import {
  dismissTrialBanner,
  subscribeTrialBannerDismissals,
  trialBannerDismissed,
  trialBannerScope,
} from '../state/trial-banner-dismissal.ts';
import { Icon } from './icons.tsx';

/** Only wraps the live-trial notice; payment and access warnings stay outside. */
export default function DismissibleTrialBanner({ children }: { children: ReactNode }) {
  const { user, company } = useSession();
  const { t } = useTranslation();
  const scope = user?.id && company?.id ? trialBannerScope(user.id, company.id, company.trialEndsAt) : null;
  const dismissed = useSyncExternalStore(
    subscribeTrialBannerDismissals,
    () => Boolean(scope && trialBannerDismissed(scope)),
    () => false,
  );
  if (dismissed) return null;

  return (
    <div className="border-b border-[color:var(--color-line)] bg-[color:var(--color-brand-soft)] print:hidden">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2 text-sm">
        {children}
        {scope ? (
          <button
            type="button"
            aria-label={t('appLayout.dismissTrialBanner')}
            title={t('appLayout.dismissTrialBanner')}
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-[color:var(--color-brand-strong)] hover:bg-[color:var(--color-brand-soft)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]"
            onClick={() => dismissTrialBanner(scope)}
          >
            <Icon name="close" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
